import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function AccountSettings() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 pb-24" data-testid="account-settings-page">
        <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Account</p>
        <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="account-settings-title">
          Settings
        </h1>
        <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-xl">
          Manage your sign-in details. For changes to billing or memorials, visit your dashboard.
        </p>

        <section className="mt-10 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="account-settings-profile">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Account</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <KV label="Name"  value={user?.name} />
            <KV label="Email" value={user?.email} />
            <KV label="Role"  value={(user?.account_type || 'family').replace('_', ' ')} />
            <KV label="Sign-in method" value={user?.google_oauth_sub ? 'Google' : 'Email + password'} />
          </div>
        </section>

        <ChangePasswordCard user={user} />
      </main>
    </div>
  );
}

function ChangePasswordCard({ user }) {
  const googleOnly = !!user?.google_oauth_sub && !user?.has_password;
  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current || !next) { toast.error('Please fill in both fields.'); return; }
    if (next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { toast.error('New passwords do not match.'); return; }
    if (next === current) { toast.error('New password must be different from the current one.'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated. You may need to sign in again on other devices.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="account-settings-change-password">
      <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Change password</h2>
      <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-xl">
        Use a strong password you don't reuse on other sites. Changing your password signs you out of any other active sessions.
      </p>
      {googleOnly ? (
        <div className="mt-5 rounded-xl bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.12)] px-4 py-3 text-sm text-[hsl(var(--primary)/0.8)]">
          This account signs in with Google. To set a local password, use{' '}
          <a href="/forgot-password" className="text-[hsl(var(--accent))] underline">Forgot password</a> first.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-5 space-y-4 max-w-md">
          <PWField label="Current password" value={current} onChange={setCurrent} testid="account-current-password" autoComplete="current-password" />
          <PWField label="New password (min 8 chars)" value={next} onChange={setNext} testid="account-new-password" autoComplete="new-password" />
          <PWField label="Confirm new password" value={confirm} onChange={setConfirm} testid="account-confirm-password" autoComplete="new-password" />
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium hover:opacity-90 transition disabled:opacity-60"
            data-testid="account-change-password-submit"
          >
            {saving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
    </section>
  );
}

function PWField({ label, value, onChange, testid, autoComplete }) {
  return (
    <label className="block">
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium mb-1">{label}</div>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="w-full text-base bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-1 py-2"
        data-testid={testid}
      />
    </label>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className="text-[hsl(var(--primary))] mt-1 text-sm">{value || '—'}</div>
    </div>
  );
}
